import { Maybe, Optional } from '../types';

type ColorCodeSchemaFunctionType = (value: number) => string;
const defaultColorCodeSchema: ColorCodeSchemaFunctionType = value => {
  if (value > 3000 && value <= 10000) {
    return '--orange';
  }

  if (value > 10000) {
    return '--red';
  }

  return '';
};

/**
 * Capitalizes only the first letter of a given string.
 *
 * @param {string} str - The input string to be capitalized.
 * @returns {string} - The string with the first letter capitalized.
 */
export const capitalizeFirstLetter = (str: string): string => {
  return str?.charAt?.(0)?.toUpperCase?.() + str?.slice?.(1);
};

export const highlightErrorSchema: ColorCodeSchemaFunctionType = value => {
  if (value > 0) {
    return '--red';
  }

  return '';
};

export const getColorVariable = (
  value: number,
  getFromSchema: ColorCodeSchemaFunctionType = defaultColorCodeSchema,
) => {
  return getFromSchema(value);
};

export const getHighlightErrorColorVariable = (value: number) => {
  return getColorVariable(value, highlightErrorSchema);
};

export const formatNumber = (value: Optional<Maybe<string | number>>) => {
  if (value === '') {
    return '-';
  }

  try {
    const numericValue = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(numericValue) || !isFinite(numericValue)) {
      return '-';
    }

    if (numericValue === 0) {
      return '0';
    }

    const formatted = new Intl.NumberFormat().format(numericValue);
    return formatted;
  } catch {
    return '-';
  }
};

type FormatDurationOptions =
  | {
      variant?: 'custom';
      showInMs?: boolean;
      showAsAPlanDecimals?: boolean;
      /** @deprecated No longer has any effect. All units now use integer composite format. */
      decimalPlaces?: 0 | 1 | 2;
      maxUnit?: 'ms' | 's' | 'min' | 'hr' | 'day' | 'year';
    }
  | { variant: 'readable'; formatter?: (value: string) => string };

export const formatDuration = (
  value: Optional<Maybe<string | number>>,
  options: FormatDurationOptions = { variant: 'custom' },
): string => {
  if (options.variant === 'readable') {
    const result = millisecondsToTimeString(Number(value));
    return options.formatter ? options.formatter(result) : result;
  }

  try {
    // Basic validations for invalid inputs
    if (value === '' || value === undefined) {
      return '-';
    }

    if (options?.showAsAPlanDecimals) {
      const timing = formatNumber(value);
      return timing;
    }

    // Convert to string and trim whitespace
    const numericString = value.toString().trim();

    // Regex /^-?\d*\.?\d*$/ matches valid numbers (e.g., "123", "-123.45", ".5").
    if (!/^-?\d*\.?\d*$/.test(numericString)) {
      return '-';
    }

    // Convert to number
    const numericValue = Number(numericString);

    // Validate for NaN, Infinity, or negative numbers
    if (isNaN(numericValue) || !isFinite(numericValue) || numericValue < 0) {
      return '-';
    }

    // Extract options with defaults
    const { showInMs = false, maxUnit = 'hr' } = options || {};

    // If showInMs is true, return milliseconds only
    if (showInMs) {
      return `${Math.round(numericValue)}ms`;
    }

    // Define thresholds and conversions
    const MS_PER_SECOND = 1000;
    const MS_PER_MINUTE = 60 * MS_PER_SECOND;
    const MS_PER_HOUR = 60 * MS_PER_MINUTE;
    const MS_PER_DAY = 24 * MS_PER_HOUR;
    const MS_PER_YEAR = 365 * MS_PER_DAY;

    // Map maxUnit to the largest unit allowed
    const unitOrder = ['ms', 's', 'min', 'hr', 'day', 'year'];
    const maxUnitIndex = unitOrder.indexOf(maxUnit);

    // All levels use integer composite format: main unit + remainder in next smaller unit.
    // ms remainder is always dropped (sub-second precision adds noise, not clarity).
    // Unit labels: ms, s, m, hr, d, y
    if (maxUnitIndex === 0 || numericValue < MS_PER_SECOND) {
      return `${Math.round(numericValue)}ms`;
    } else if (maxUnitIndex === 1 || numericValue < MS_PER_MINUTE) {
      const wholeSeconds = Math.floor(numericValue / MS_PER_SECOND);
      return `${wholeSeconds}s`;
    } else if (maxUnitIndex === 2 || numericValue < MS_PER_HOUR) {
      const wholeMinutes = Math.floor(numericValue / MS_PER_MINUTE);
      const remainingSeconds = Math.floor(
        (numericValue % MS_PER_MINUTE) / MS_PER_SECOND,
      );
      return remainingSeconds > 0
        ? `${wholeMinutes}m ${remainingSeconds}s`
        : `${wholeMinutes}m`;
    } else if (maxUnitIndex === 3 || numericValue < MS_PER_DAY) {
      const wholeHours = Math.floor(numericValue / MS_PER_HOUR);
      const remainingMinutes = Math.floor(
        (numericValue % MS_PER_HOUR) / MS_PER_MINUTE,
      );
      return remainingMinutes > 0
        ? `${wholeHours}hr ${remainingMinutes}m`
        : `${wholeHours}hr`;
    } else if (maxUnitIndex === 4 || numericValue < MS_PER_YEAR) {
      const days = Math.floor(numericValue / MS_PER_DAY);
      const remainingHours = Math.floor(
        (numericValue % MS_PER_DAY) / MS_PER_HOUR,
      );
      return remainingHours > 0 ? `${days}d ${remainingHours}hr` : `${days}d`;
    } else {
      const years = Math.floor(numericValue / MS_PER_YEAR);
      const remainingDays = Math.floor(
        (numericValue % MS_PER_YEAR) / MS_PER_DAY,
      );
      return remainingDays > 0 ? `${years}y ${remainingDays}d` : `${years}y`;
    }
  } catch {
    return '-';
  }
};

export const millisecondsToTimeString = (
  ms: number | undefined | null | string,
) => {
  const numMs = typeof ms === 'string' ? parseInt(ms) : ms;

  if (ms === undefined || ms === null || isNaN(numMs) || numMs < 0) {
    return '-';
  }

  // Handle case for less than 1 second
  if (numMs < 1000) {
    return `${numMs}ms`;
  }

  // Convert milliseconds to seconds
  const totalSeconds: number = numMs / 1000;

  // Get whole minutes
  const minutes: number = Math.floor(totalSeconds / 60);

  // Get remaining seconds, rounded
  const seconds: number = Math.round(totalSeconds % 60);

  // Case 1: Exact minutes (no seconds)
  if (minutes > 0 && seconds === 0) {
    return `${minutes}m`;
  }

  // Case 2: Only seconds (no minutes)
  if (minutes === 0) {
    return `${seconds}s`;
  }

  // Case 3: Minutes and seconds
  return `${minutes}m ${seconds}s`;
};

export const parseValueByType = (
  value: string | number,
  type: string,
): string | number => {
  if (type === 'number' && typeof value === 'string') {
    return parseInt(value?.trim(), 10);
  }

  return value;
};

/**
 * Parses a number and formats it as a percentage string.
 */
export const parsePercentage = (value: number): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 0,
  });
  return formatter.format(value);
};

/**
 * Parses a number and formats it as a decimal string.
 */
export const parseDecimal = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
  }).format(value);
};

/**
 * Removes leading and trailing quote characters (single or double) from a string.
 * Also trims any surrounding whitespace before removing quotes.
 * Returns `undefined` if the input is `undefined`.
 */
export const removeQuotesFromText = (
  str: string | undefined,
): string | undefined => {
  return str?.trim().replace(/^(["']+)|(["']+)$/g, '');
};

/**
 * Extracts the substring found between two delimiter strings within a given text.
 * The search for `end` begins after the position where `start` was found.
 * Returns an empty string if either delimiter is not found.
 *
 * @example
 * getTextBetween('<b>', '</b>', 'Hello <b>world</b>!')  // "world"
 * getTextBetween('[', ']', 'Click [here] please')       // "here"
 * getTextBetween('<b>', '</b>', 'No tags here')          // ""
 * getTextBetween('<b>', '</b>', '<b>no closing')         // ""
 */
export const getTextBetween = (start: string, end: string, text: string) => {
  const startIndex = text.indexOf(start);
  if (startIndex === -1) return ''; // Start string not found

  const endIndex = text.indexOf(end, startIndex + start.length);
  if (endIndex === -1) return ''; // End string not found

  // Extract the text between start and end
  return text.substring(startIndex + start.length, endIndex).trim();
};

/**
 * Formats a numeric or string millisecond value into a human-readable duration string
 * by appending the `ms` unit suffix.
 *
 * @example
 * formatMilliseconds(120)    // "120ms"
 * formatMilliseconds(0)      // "0ms"
 * formatMilliseconds("350")  // "350ms"
 */
export const formatMilliseconds = (milliseconds: number | string): string => {
  return `${milliseconds}ms`;
};

/**
 * Formats a numeric value into a compact, human-readable string.
 *
 * - Values below 100,000 are returned as plain numbers (e.g. `800` → `"800"`, `44000` → `"44000"`).
 * - Values from 100,000 to 999,999 are formatted with a `K` suffix (e.g. `100000` → `"100K"`, `450000` → `"450K"`).
 * - Values from 1,000,000 and above are formatted with an `M` suffix (e.g. `1_120_000` → `"1.12M"`).
 *
 * Trailing zeros are removed from the decimal part (e.g. `1.10M` → `"1.1M"`, `1.00M` → `"1M"`).
 *
 * @example
 * formatCompactNumber(800)       // "800"
 * formatCompactNumber(1200)      // "1200"
 * formatCompactNumber(44000)     // "44000"
 * formatCompactNumber(99999)     // "99999"
 * formatCompactNumber(100000)    // "100K"
 * formatCompactNumber(450000)    // "450K"
 * formatCompactNumber(1000000)   // "1M"
 * formatCompactNumber(1120000)   // "1.12M"
 */
export const formatCompactNumber = (value: number): string => {
  if (value >= 1_000_000) {
    const formatted = value / 1_000_000;
    return `${parseFloat(formatted.toFixed(2))}M`;
  }
  if (value >= 100_000) {
    const formatted = value / 1_000;
    return `${parseFloat(formatted.toFixed(1))}K`;
  }

  return value.toLocaleString();
};

/**
 * Converts a number (1-999) to its English word representation with capitalized first letter.
 *
 * @example
 * numberToWords(1) // 'One'
 * numberToWords(42) // 'Forty Two'
 * numberToWords(100) // 'One Hundred'
 */
export const numberToWords = (num: number): string => {
  // Validate input: return empty string for invalid inputs
  if (
    !Number.isFinite(num) ||
    !Number.isInteger(num) ||
    num < 1 ||
    num >= 1000
  ) {
    return '';
  }

  const ones = [
    '',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
  ];
  const teens = [
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
  ];
  const tens = [
    '',
    '',
    'Twenty',
    'Thirty',
    'Forty',
    'Fifty',
    'Sixty',
    'Seventy',
    'Eighty',
    'Ninety',
  ];

  if (num < 10) return ones[num];
  if (num < 20) return teens[num - 10];
  if (num < 100) {
    const tenDigit = Math.floor(num / 10);
    const oneDigit = num % 10;
    return (tens[tenDigit] + (oneDigit ? ' ' + ones[oneDigit] : '')).trim();
  }
  if (num < 1000) {
    const hundred = Math.floor(num / 100);
    const remainder = num % 100;
    return (
      ones[hundred] +
      ' Hundred' +
      (remainder ? ' ' + numberToWords(remainder) : '')
    ).trim();
  }
  return '';
};

/**
 * Coerces a possibly-absent value into a trimmed string, returning `''` for
 * anything that is not a string (`null`, `undefined`, numbers, objects).
 *
 * Use it to normalise optional API fields before a truthiness check, so a
 * whitespace-only value reads as "absent" like a `null` does.
 *
 * @example
 * toTrimmedString('  hello  ')  // 'hello'
 * toTrimmedString('   ')        // ''
 * toTrimmedString(null)         // ''
 * toTrimmedString(undefined)    // ''
 */
export const toTrimmedString = (value?: string | null): string => {
  return typeof value === 'string' ? value.trim() : '';
};

type RichTextNode = { text?: unknown; children?: unknown[] };

const hasTextInNodes = (nodes: unknown[]): boolean => {
  return nodes.some(node => {
    const { text, children } = (node ?? {}) as RichTextNode;

    if (typeof text === 'string') {
      return text.trim() !== '';
    }

    if (Array.isArray(children)) {
      return hasTextInNodes(children);
    }

    return false;
  });
};

/**
 * True when a rich-text editor value (the `TextEditor` node array) carries
 * content: somewhere in the tree there is a text leaf that is not blank.
 * Nested blocks (lists, quotes) are searched recursively, so a list holding
 * only empty items reads as blank. Anything that is not an array reads as
 * blank too, so a missing value, an empty array and an editor holding one
 * empty paragraph all count the same. The websheet templates' own
 * `isTextChildrenEmpty` is its inverse.
 *
 * @example
 * hasRichTextContent([{ type: 'paragraph', children: [{ text: 'Hi' }] }]) // true
 * hasRichTextContent([{ type: 'paragraph', children: [{ text: '   ' }] }]) // false
 * hasRichTextContent([])                                                   // false
 * hasRichTextContent(undefined)                                            // false
 */
export const hasRichTextContent = (value: unknown): boolean => {
  if (!Array.isArray(value)) {
    return false;
  }

  return value.some(block => {
    const { children } = (block ?? {}) as RichTextNode;

    return Array.isArray(children) && hasTextInNodes(children);
  });
};
